import {
  makeStyles,
  tokens,
  Text,
  Card,
  CardHeader,
  Badge,
  Divider,
} from '@fluentui/react-components';
import { QualityIndicator, QualityBadge } from '../common/QualityIndicator';
import { useTimezone } from '../common/TimezoneProvider';
import { TeamsParticipantsCard } from './TeamsParticipantsCard';
import { TeamsQualityCard } from './TeamsQualityCard';
import type { CallDetail as CallDetailType, QoSStream } from '../../types';
import { durationSourceLabel, reportingTimeLabel } from '../../utils/callDisplay';

const useStyles = makeStyles({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: tokens.spacingHorizontalL,
    marginBottom: tokens.spacingVerticalL,
  },
  card: {
    minHeight: '200px',
  },
  cardContent: {
    padding: tokens.spacingHorizontalM,
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: '140px 1fr',
    gap: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
  },
  label: {
    color: tokens.colorNeutralForeground3,
  },
  value: {
    fontWeight: tokens.fontWeightSemibold,
  },
  phoneNumber: {
    fontFamily: 'monospace',
  },
  qosCard: {
    gridColumn: '1 / -1',
  },
  qosGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: tokens.spacingHorizontalL,
  },
  streamSection: {
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  streamTitle: {
    marginBottom: tokens.spacingVerticalM,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  metricRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalXS} 0`,
  },
  qualityHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: tokens.spacingVerticalM,
  },
});

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
}

interface QoSStreamCardProps {
  title: string;
  stream: QoSStream;
  direction: 'forward' | 'backward';
}

function QoSStreamSection({ title, stream, direction }: QoSStreamCardProps) {
  const styles = useStyles();

  const metrics = [
    { label: 'Avg Jitter', value: stream.avg_jitter_ms, unit: 'ms' },
    { label: 'Max Jitter', value: stream.max_jitter_ms, unit: 'ms' },
    { label: 'Avg Latency', value: stream.avg_latency_ms, unit: 'ms' },
    { label: 'Max Latency', value: stream.max_latency_ms, unit: 'ms' },
    { label: 'Packet Loss', value: stream.packet_loss_percent, unit: '%' },
    { label: 'Packets Lost', value: stream.packets_lost, unit: '' },
    { label: 'Total Packets', value: stream.total_packets, unit: '' },
  ];

  return (
    <div className={styles.streamSection}>
      <div className={styles.streamTitle}>
        <Text weight="semibold">{title}</Text>
        <Badge appearance="outline" size="small">
          {direction === 'forward' ? 'Inbound Leg' : 'Outbound Leg'}
        </Badge>
      </div>
      {metrics.map((metric) => (
        <div key={metric.label} className={styles.metricRow}>
          <Text className={styles.label}>{metric.label}</Text>
          <Text className={styles.value}>
            {metric.value !== null ? `${metric.value}${metric.unit}` : '-'}
          </Text>
        </div>
      ))}
    </div>
  );
}

interface CallEvidenceCardsProps {
  call: CallDetailType;
  canSeeTechnical: boolean;
  locationLabel: string;
}

export function CallEvidenceCards({ call, canSeeTechnical, locationLabel }: CallEvidenceCardsProps) {
  const styles = useStyles();
  const { formatDateTime } = useTimezone();

  return (
    <div className={styles.grid}>
      {/* Call Info Card */}
      <Card className={styles.card}>
        <CardHeader header={<Text weight="semibold">Call Information</Text>} />
        <div className={styles.cardContent}>
          <div className={styles.infoGrid}>
            <Text className={styles.label}>Session ID</Text>
            <Text className={styles.value} style={{ fontFamily: 'monospace', fontSize: '12px' }}>
              {call.session_id}
            </Text>

            <Text className={styles.label}>Actual Start</Text>
            <Text className={styles.value}>{formatDateTime(call.start_time)}</Text>

            {call.reporting_time_basis === 'completion' && (
              <>
                <Text className={styles.label}>{reportingTimeLabel(call.reporting_time_basis)}</Text>
                <Text className={styles.value}>{formatDateTime(call.reporting_time)}</Text>
              </>
            )}

            <Text className={styles.label}>End Time</Text>
            <Text className={styles.value}>{formatDateTime(call.end_time)}</Text>

            <Text className={styles.label}>Duration</Text>
            <Text className={styles.value}>
              {formatDuration(call.duration_seconds)}
              {durationSourceLabel(call.duration_source) ? ` (${durationSourceLabel(call.duration_source)})` : ''}
            </Text>

            {call.incomplete_leg_evidence && (
              <>
                <Text className={styles.label}>Journey Timing</Text>
                <Text className={styles.value}>Partial — one or more actual starts are unavailable</Text>
              </>
            )}

            <Text className={styles.label}>Terminate Cause</Text>
            <Text className={styles.value}>{call.terminate_cause || '-'}</Text>
          </div>
        </div>
      </Card>

      {/* Parties Card */}
      <Card className={styles.card}>
        <CardHeader header={<Text weight="semibold">Call Parties</Text>} />
        <div className={styles.cardContent}>
          <div className={styles.infoGrid}>
            <Text className={styles.label}>Calling Number</Text>
            <Text className={`${styles.value} ${styles.phoneNumber}`}>
              {call.calling_number || '-'}
            </Text>

            <Text className={styles.label}>Calling Name</Text>
            <Text className={styles.value}>{call.calling_name || '-'}</Text>

            <Text className={styles.label}>Called Number</Text>
            <Text className={`${styles.value} ${styles.phoneNumber}`}>
              {call.called_number || '-'}
            </Text>

            <Text className={styles.label}>{locationLabel}</Text>
            <Text className={styles.value}>{call.branch_name || '-'}</Text>

            <Text className={styles.label}>Assignment</Text>
            <Text className={styles.value}>{call.assignment_name || '-'}</Text>
          </div>
        </div>
      </Card>

      {/* Technical Details (if user has access) */}
      {canSeeTechnical && (
        <Card className={styles.card}>
          <CardHeader header={<Text weight="semibold">Technical Details</Text>} />
          <div className={styles.cardContent}>
            <div className={styles.infoGrid}>
              <Text className={styles.label}>Ingress Group</Text>
              <Text className={styles.value}>{call.ingress_sig_group || '-'}</Text>

              <Text className={styles.label}>Egress Group</Text>
              <Text className={styles.value}>{call.egress_sig_group || '-'}</Text>

              <Text className={styles.label}>Ingress Address</Text>
              <Text className={styles.value}>{call.ingress_remote_addr || '-'}</Text>

              <Text className={styles.label}>Egress Address</Text>
              <Text className={styles.value}>{call.egress_remote_addr || '-'}</Text>

              <Text className={styles.label}>Setup Time</Text>
              <Text className={styles.value}>
                {call.setup_time_ms ? `${call.setup_time_ms}ms` : '-'}
              </Text>

              <Text className={styles.label}>Post Dial Delay</Text>
              <Text className={styles.value}>
                {call.post_dial_delay_ms ? `${call.post_dial_delay_ms}ms` : '-'}
              </Text>
            </div>
          </div>
        </Card>
      )}

      {/* Teams Participants Card */}
      {call.teams_data && (
        <TeamsParticipantsCard teamsData={call.teams_data} />
      )}

      {/* Teams Quality Card - Full Width */}
      {call.teams_data && (
        <TeamsQualityCard
          qualityStreams={call.teams_data.quality_streams}
          className={styles.qosCard}
        />
      )}

      {/* QoS Card - Full Width */}
      <Card className={`${styles.card} ${styles.qosCard}`}>
        <CardHeader
          header={
            <div className={styles.qualityHeader}>
              <Text weight="semibold">Quality of Service (QoS)</Text>
              <QualityBadge status={call.quality_rating.overall} />
            </div>
          }
        />
        <div className={styles.cardContent}>
          <div className={styles.qosGrid}>
            <QoSStreamSection
              title="Forward Stream"
              stream={call.forward_stream}
              direction="forward"
            />
            <QoSStreamSection
              title="Backward Stream"
              stream={call.backward_stream}
              direction="backward"
            />
          </div>

          <Divider style={{ margin: `${tokens.spacingVerticalL} 0` }} />

          <div className={styles.infoGrid}>
            <Text className={styles.label}>Jitter Status</Text>
            <QualityIndicator status={call.quality_rating.jitter_status} showLabel />

            <Text className={styles.label}>Latency Status</Text>
            <QualityIndicator status={call.quality_rating.latency_status} showLabel />

            <Text className={styles.label}>Packet Loss Status</Text>
            <QualityIndicator status={call.quality_rating.packet_loss_status} showLabel />
          </div>
        </div>
      </Card>
    </div>
  );
}
